import React from 'react';
import { useTranslation } from 'react-i18next';
import { Languages, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { LanguageCode, LANGUAGE_STORAGE_KEY, SUPPORTED_LANGUAGES, type LanguageCodeValue } from '@/lib/i18n/languageConstants';

const LANGUAGE_LABEL_KEYS: Record<LanguageCodeValue, string> = {
  [LanguageCode.Tr]: 'header.language.tr',
  [LanguageCode.En]: 'header.language.en',
};

/** Aktif dilin kısa kodu — menü tetikleyicisinde yardımcı metin olarak gösterilir. */
export function useCurrentLanguage(): LanguageCodeValue {
  const { i18n } = useTranslation();
  return i18n.language as LanguageCodeValue;
}

export function useChangeLanguage(): (lang: LanguageCodeValue) => void {
  const { i18n } = useTranslation();
  return React.useCallback(
    (lang: LanguageCodeValue) => {
      if (lang === i18n.language) return;
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      i18n.changeLanguage(lang);
    },
    [i18n],
  );
}

/**
 * Dil seçeneklerinin kendisi — hem bağımsız LanguageSwitcher hem de kullanıcı
 * menüsündeki alt menü tarafından kullanılır, böylece seçim listesi tek yerde durur.
 */
export function LanguageMenuItems() {
  const { t } = useTranslation();
  const currentLang = useCurrentLanguage();
  const changeLanguage = useChangeLanguage();

  return (
    <>
      {SUPPORTED_LANGUAGES.map((lang) => (
        <DropdownMenuItem
          key={lang}
          onClick={() => changeLanguage(lang)}
          className="flex items-center gap-2"
        >
          <span className="flex-1">{t(LANGUAGE_LABEL_KEYS[lang])}</span>
          {lang === currentLang && <Check size={14} aria-hidden="true" />}
        </DropdownMenuItem>
      ))}
    </>
  );
}

export function LanguageSwitcher() {
  const { t } = useTranslation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('header.language.toggle.aria')}
          className={cn(
            'inline-flex items-center justify-center rounded-lg p-1.5 transition-[background-color,color,transform] duration-150 ease-out-strong motion-safe:active:scale-[0.94]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring border',
            'bg-transparent border-border text-muted-foreground hover:bg-accent'
          )}
        >
          <Languages size={18} aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-32">
        <DropdownMenuLabel>{t('header.language.label')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <LanguageMenuItems />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
